import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { environment } from '@environments/environment'
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from 'rxjs'

/**
 * Interface defining a beta feature configuration
 */
export interface BetaFeature {
  id?: number
  flag: string
  label: string
  description: string
  default: boolean
  permanent: boolean
  /** User's explicit enabled state (0 = disabled, 1 = enabled) - only present in user flags */
  enabled?: number | boolean
}

/**
 * Service that manages beta feature flags.
 * Features are stored individually and can be toggled by super admins.
 *
 */
@Injectable({
  providedIn: 'root'
})
export class BetaFeaturesService {
  /** BehaviorSubject to emit feature changes */
  private _featuresSubject = new BehaviorSubject<BetaFeature[]>([])

  /** Synchronous access to current features */
  get availableFeatures(): BetaFeature[] {
    return this._featuresSubject.getValue()
  }

  constructor(
    private _http: HttpClient,
    private auth: AuthService
  ) {}

  /**
   * Fetches features from the API (private - use getFeatures())
   */
  private _fetchFeatures(): Observable<BetaFeature[]> {
    return this._http.get<Response<{ flags: BetaFeature[] }>>(`${environment.v2.baseUrl}generic/flag`).pipe(
      map((response) => response.data.flags),
      tap((features) => {
        this._featuresSubject.next(features)
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    )
  }

  /**
   * @returns new beta feature
   */
  addFeature(betaFeatureData: BetaFeature) {
    return this._http.post<Response<{ flag: BetaFeature }>>(`${environment.v2.baseUrl}generic/flag`, betaFeatureData)
  }

  /**
   * @returns updated beta feature
   */
  editFeature(betaFeatureData: BetaFeature) {
    return this._http.put<Response<{ flag: BetaFeature }>>(`${environment.v2.baseUrl}generic/flag/${betaFeatureData.id}`, betaFeatureData)
  }

  /**
   * @returns deleted beta feature
   */
  deleteFeature(flagId: number) {
    return this._http.delete<Response<{ flag: BetaFeature }>>(`${environment.v2.baseUrl}generic/flag/${flagId}`)
  }

  /**
   * Toggles a feature flag on or off for the current user
   */
  toggleFeature(flagId: number): Observable<BetaFeature> {
    return this._http.post<Response<{ flag: BetaFeature }>>(`${environment.v2.baseUrl}users/flag`, { flag_id: flagId }).pipe(map((response) => response.data.flag))
  }

  /**
   * Reloads beta features from the API. Call this after user authenticates.
   */
  reloadFeatures() {
    return this._fetchFeatures()
  }

  /**
   * Computes a comma-separated string of enabled beta feature flag keys for the current user.
   *
   * This method is used for setting the X-Beta-Features header on outgoing requests.
   * For each feature, checks the user's explicit enabled state first, then falls back to default.
   *
   * @returns {string} Comma-separated enabled feature flag keys (may be empty string if none)
   */
  getEnabledFeaturesHeader(): string {
    const userFlags = this.auth.flags ?? []

    const enabledFlags = this.availableFeatures
      .filter((feature) => {
        const userFlag = userFlags.find((uf) => uf.id === feature.id)
        // If user has explicit choice, use it (convert 0/1 to boolean)
        if (userFlag) return Boolean(userFlag.enabled)
        // Otherwise use default
        return feature.default
      })
      .map((feature) => feature.flag)

    return enabledFlags.join(',')
  }

  /**
   * Returns non-permanent features with their enabled state calculated for the current user.
   * Used by the beta features modal to display toggleable flags.
   *
   * @returns {BetaFeature[]} Array of non-permanent features with enabled state set
   */
  getToggleableFeatures(): BetaFeature[] {
    const userFlags = this.auth.flags ?? []
    return this.availableFeatures
      .filter((feature) => !feature.permanent)
      .map((feature) => {
        const userFlag = userFlags.find((uf) => uf.id === feature.id)
        return {
          ...feature,
          // If user has explicit choice, use it (convert 0/1 to boolean), otherwise use default
          enabled: userFlag ? Boolean(userFlag.enabled) : feature.default
        }
      })
  }

  /**
   * Determines if a given beta feature flag is enabled for the current user.
   *
   * Logic:
   *   - If the feature is "permanent": returns the default value (user cannot change it)
   *   - If the feature is NOT permanent:
   *     - If user has explicit choice (enabled property), use it
   *     - Otherwise → returns the default value
   *
   * @param flagKey The unique key (string) for the feature flag to check.
   * @returns {boolean} True if the feature is enabled for the user, false otherwise.
   */
  isFeatureEnabled(flagKey: string): boolean {
    const featureFlag = this.availableFeatures.find((feature) => feature.flag === flagKey)

    // Return false if feature flag doesn't exist
    if (!featureFlag) return false

    // Permanent flags always return the default value (user cannot change)
    if (featureFlag.permanent) {
      return featureFlag.default
    }

    // Check if user has explicit choice for this flag
    const userFlag = this.auth.flags?.find((uf) => uf.flag === flagKey)

    // If user has explicit choice, use it (convert 0/1 to boolean), otherwise use default
    return userFlag ? Boolean(userFlag.enabled) : featureFlag.default
  }
}
